import { isHexColor } from "./color";
import {
  INLINE,
  POLITE_SLIDE_IN,
  POPUP,
  STICKY_SIDEBAR,
} from "./constants";
import { getShareUrl } from "./shareLinks";

export type EmbedLayoutId =
  | typeof STICKY_SIDEBAR
  | typeof POLITE_SLIDE_IN
  | typeof POPUP
  | typeof INLINE;
export type IntegratePane = "embed" | "share" | "email" | "connections";

export const EMBED_LAYOUT_TYPES = [
  {
    id: STICKY_SIDEBAR,
    key: "sidebar",
    labelKey: "integrate.embed.sidebar",
    legacyLabelKey: "form.stickySidebar",
  },
  {
    id: POLITE_SLIDE_IN,
    key: "popover",
    labelKey: "integrate.embed.popover",
    legacyLabelKey: "form.politeSlideIn",
  },
  {
    id: POPUP,
    key: "popup",
    labelKey: "integrate.embed.popup",
    legacyLabelKey: "form.popup",
  },
  {
    id: INLINE,
    key: "standard",
    labelKey: "integrate.embed.standard",
    legacyLabelKey: "form.inline",
  },
] as const satisfies ReadonlyArray<{
  id: EmbedLayoutId;
  key: string;
  labelKey: string;
  legacyLabelKey: string;
}>;

export interface EmbedLayout {
  id: EmbedLayoutId;
  minimizedTitle?: string;
  isLeftAligned?: boolean;
  isRightAligned?: boolean;
  allowMinimize?: boolean;
  formWidth?: number;
  formHeight?: number;
  buttonColor?: string;
  buttonType?: "text" | "icon" | "textIcon";
  buttonSize?: "small" | "medium" | "large";
  buttonIcon?: string;
  cornerRadius?: { tl: number; tr: number; bl: number; br: number };
  fontSize?: number;
}

export interface EmbedSettings {
  layout: EmbedLayout;
  triggerType: string;
  triggerValue: string;
  activationType: string;
  activationValue: string;
  deactivationType: string;
  deactivationValue: string;
  checkForCookieConsent: boolean;
  cookieConsentProvider: string;
  modalHeight: number | undefined;
}

export interface StoredEmbedSettings {
  selectedLayout: EmbedLayout & { name?: string };
  selectedTriggerType: string;
  selectedActivation: string;
  selectedDeactivation: string;
  checkForCookieConsent: boolean;
  cookieConsentProvider: string;
  fields: Record<string, string | undefined>;
}

export interface EmbedCopyArtifacts {
  storageKey: string;
  storageValue: string;
  analytics: {
    layoutId: EmbedLayoutId;
    layoutName: string;
    allowMinimize: boolean | undefined;
    minimizedTitle: string | undefined;
    isLeftAligned: boolean | undefined;
    isRightAligned: boolean | undefined;
    selectedTriggerType: string;
    selectedActivation: string;
    selectedDeactivation: string;
    showOnScrolling: string;
    showAfter: string;
    activateOnVisit: string;
    deactivateAfter: string;
    checkForCookieConsent: string;
    cookieConsentProvider: string | null;
  };
}

export interface EmbedContext {
  embedUrl: string;
  scriptBaseUrl: string;
  widgetId: string;
  formName: string;
  borderRadius: number;
  iframeHeight: number | undefined;
  formStyleWidth?: number;
}

export interface BasicEmbedCodeContext {
  baseUrl: string;
  fallbackScriptBaseUrl: string;
  isWhiteLabelled: boolean;
  environment: string | undefined;
  widgetName: string;
  widgetId: string;
  checkForCookieConsent: boolean;
  cookieConsentProvider: string;
}

export type EmbedScriptBaseUrlContext = Pick<
  BasicEmbedCodeContext,
  "baseUrl" | "fallbackScriptBaseUrl" | "isWhiteLabelled" | "environment"
>;

function serializeNewString(value: string): string {
  const jsonContent = JSON.stringify(value)
    .slice(1, -1)
    .replace(/\\"|['"&<>]/g, (character) => {
      const escapes: Record<string, string> = {
        "'": "\\u0027",
        '"': "\\u0022",
        '\\"': "\\u0022",
        "&": "\\u0026",
        "<": "\\u003c",
        ">": "\\u003e",
      };
      return escapes[character];
    });
  return `'${jsonContent}'`;
}

function escapeHtmlAttribute(value: string | number | boolean | undefined): string {
  const entities: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return String(value).replace(/[&<>"']/g, (character) => entities[character]);
}

function isInt(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

export function buildEmbedCopyArtifacts({
  widgetId,
  settings,
  layoutName,
}: {
  widgetId: string;
  settings: StoredEmbedSettings;
  layoutName: string;
}): EmbedCopyArtifacts {
  // Match the legacy modal's event properties, including its selected-field
  // mapping for both trigger and activation value keys.
  const triggerValue = settings.fields[settings.selectedTriggerType] || "";
  const activationValue = settings.fields[settings.selectedActivation] || "";
  return {
    storageKey: `embedCode${widgetId}`,
    storageValue: JSON.stringify(settings),
    analytics: {
      layoutId: settings.selectedLayout.id,
      layoutName,
      allowMinimize: settings.selectedLayout.allowMinimize,
      minimizedTitle: settings.selectedLayout.minimizedTitle,
      isLeftAligned: settings.selectedLayout.isLeftAligned,
      isRightAligned: settings.selectedLayout.isRightAligned,
      selectedTriggerType: settings.selectedTriggerType,
      selectedActivation: settings.selectedActivation,
      selectedDeactivation: settings.selectedDeactivation,
      showOnScrolling: triggerValue,
      showAfter: triggerValue,
      activateOnVisit: activationValue,
      deactivateAfter: activationValue,
      checkForCookieConsent: String(settings.checkForCookieConsent),
      cookieConsentProvider: settings.checkForCookieConsent
        ? settings.cookieConsentProvider
        : null,
    },
  };
}

export function getIframeId(layout: EmbedLayout): string {
  if (layout.id === "POPUP") return "popup";
  if (layout.id === "INLINE") return "inline";

  const side = layout.isLeftAligned ? "left" : "right";
  return layout.id === "POLITE_SLIDE_IN"
    ? `polite-slide-in-${side}`
    : `sticky-sidebar-${side}`;
}

export function buildDataLayout(layout: EmbedLayout, formStyleWidth?: number): string {
  const legacyLayout = {
    id: layout.id,
    minimizedTitle: layout.minimizedTitle,
    isLeftAligned: layout.isLeftAligned,
    isRightAligned: layout.isRightAligned,
    allowMinimize: layout.allowMinimize,
  };
  const additions: string[] = [];

  if (isInt(layout.formWidth, 100, 2000) && layout.formWidth !== formStyleWidth)
    additions.push(`'formWidth':${layout.formWidth}`);
  if (layout.buttonColor !== undefined && isHexColor(layout.buttonColor)) {
    additions.push(`'buttonColor':${serializeNewString(layout.buttonColor.trim())}`);
  }
  if (layout.buttonType !== undefined) {
    additions.push(`'buttonType':${serializeNewString(layout.buttonType)}`);
  }
  if (layout.buttonSize !== undefined) {
    additions.push(`'buttonSize':${serializeNewString(layout.buttonSize)}`);
  }
  if (layout.buttonIcon !== undefined) {
    additions.push(`'buttonIcon':${serializeNewString(layout.buttonIcon)}`);
  }
  if (layout.cornerRadius !== undefined) {
    additions.push(
      `'cornerRadius':${JSON.stringify(layout.cornerRadius).replace(/"/g, "'")}`
    );
  }
  if (isInt(layout.fontSize, 8, 100)) additions.push(`'fontSize':${layout.fontSize}`);

  const legacyJson = `{${Object.entries(legacyLayout)
    .flatMap(([key, value]) =>
      value === undefined
        ? []
        : [
            `'${key}':${
              typeof value === "string"
                ? serializeNewString(value)
                : JSON.stringify(value).replace(/"/g, "'")
            }`,
          ]
    )
    .join(",")}}`;
  return additions.length === 0
    ? legacyJson
    : `${legacyJson.slice(0, -1)},${additions.join(",")}}`;
}

export function buildEmbedCode(settings: EmbedSettings, context: EmbedContext): string {
  const iframeId = getIframeId(settings.layout);
  const isAlwaysVisible =
    settings.layout.id === "INLINE" &&
    settings.triggerType === "alwaysShow" &&
    settings.activationType === "alwaysActivated" &&
    settings.deactivationType === "neverDeactivate";
  const displayStyle = isAlwaysVisible ? "" : "display:none;";
  const modalHeightAttribute =
    settings.layout.id !== "INLINE" && settings.modalHeight !== undefined
      ? `data-modal-height="${escapeHtmlAttribute(settings.modalHeight)}"`
      : "";

  return `<iframe
    src="${escapeHtmlAttribute(context.embedUrl)}"
    style="${displayStyle}width:100%;height:100%;border:none;border-radius:${escapeHtmlAttribute(context.borderRadius)}px"
    id="${escapeHtmlAttribute(`${iframeId}-${context.widgetId}`)}"
    data-layout="${buildDataLayout(settings.layout, context.formStyleWidth)}"
    data-trigger-type="${escapeHtmlAttribute(settings.triggerType)}"
    data-trigger-value="${escapeHtmlAttribute(settings.triggerValue)}"
    data-activation-type="${escapeHtmlAttribute(settings.activationType)}"
    data-activation-value="${escapeHtmlAttribute(settings.activationValue)}"
    data-deactivation-type="${escapeHtmlAttribute(settings.deactivationType)}"
    data-deactivation-value="${escapeHtmlAttribute(settings.deactivationValue)}"
    data-form-name="${escapeHtmlAttribute(context.formName)}"
    data-height="${escapeHtmlAttribute(context.iframeHeight)}"
    data-layout-iframe-id="${escapeHtmlAttribute(`${iframeId}-${context.widgetId}`)}"
    data-form-id="${escapeHtmlAttribute(context.widgetId)}"
    data-cookie-consent="${settings.checkForCookieConsent}"
    ${
      settings.checkForCookieConsent
        ? `data-cookie-consent-provider="${escapeHtmlAttribute(settings.cookieConsentProvider)}"`
        : ""
    }
    title="${escapeHtmlAttribute(context.formName)}"
    ${modalHeightAttribute}
        >
</iframe>
<script src="${escapeHtmlAttribute(`${context.scriptBaseUrl}/form_embed.js`)}"><\/script>`;
}

export function buildBasicEmbedCode(context: BasicEmbedCodeContext): string {
  const embedUrl = getShareUrl(
    context.baseUrl,
    context.widgetName,
    context.widgetId,
    context.environment
  );
  const scriptBaseUrl = getEmbedScriptBaseUrl(context);
  const cookieConsentAttributes = context.checkForCookieConsent
    ? `data-cookie-consent="true" data-cookie-consent-provider="${escapeHtmlAttribute(context.cookieConsentProvider)}"`
    : `data-cookie-consent="false"`;

  return `<iframe src="${escapeHtmlAttribute(embedUrl)}" style="border:none;width:100%;" scrolling="no" id="${escapeHtmlAttribute(context.widgetId)}" title="${escapeHtmlAttribute(context.widgetName)}" ${cookieConsentAttributes}></iframe>\n<script src="${escapeHtmlAttribute(`${scriptBaseUrl}/form_embed.js`)}"><\/script>`;
}

export function getEmbedScriptBaseUrl(
  context: EmbedScriptBaseUrlContext
): string {
  return context.isWhiteLabelled && context.environment !== "beta"
    ? `${context.baseUrl}/js`
    : context.fallbackScriptBaseUrl;
}
